/**
 * Which docx editor engine the app mounts.
 *
 * - `eigenpal`: `@eigenpal/docx-editor-*` 1.8.3 (Apache-2.0, review built in:
 *   Suggesting mode, tracked-change cards, comments, agent tool ref). This is
 *   the default and the only engine the review, recovery and live-agent
 *   features target.
 * - `legacy`: `@docx-editor.dev/*` 2.10, the previous surface. Kept behind the
 *   flag as a fallback while the pin is verified on real drafts. It paints
 *   markup but cannot resolve it, so review stays server-side there.
 *
 * Resolution order: a per-device override in localStorage (for QA), then the
 * build-time env, then `eigenpal`.
 */
export type DocxEditorEngine = "eigenpal" | "legacy";

export const DOCX_ENGINE_STORAGE_KEY = "caseblink.docxEditor.engine";

const isEngine = (value: unknown): value is DocxEditorEngine =>
  value === "eigenpal" || value === "legacy";

export const docxEditorEngine = (): DocxEditorEngine => {
  try {
    const override = window.localStorage.getItem(DOCX_ENGINE_STORAGE_KEY);
    if (isEngine(override)) return override;
  } catch {
    // Storage may be unavailable (private mode, sandboxed iframe); fall through.
  }
  const fromEnv = import.meta.env?.VITE_DOCX_EDITOR_ENGINE;
  return isEngine(fromEnv) ? fromEnv : "eigenpal";
};
